import { 
  ShoppingCart, 
  ShoppingBag, 
  Database, 
  Trash2, 
  ArrowRightLeft, 
  Edit 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { useNavigation } from "@/components/layout/app-layout";

interface MovementTypeCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

function MovementTypeCard({ icon, title, description, onClick }: MovementTypeCardProps) {
  return (
    <Card 
      className="group relative cursor-pointer transition-all duration-200 hover:border-primary hover:shadow-lg focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Select ${title}: ${description}`}
    >
      <div className="p-6 flex flex-col items-start gap-4">
        {/* Icon */}
        <div className="text-primary">
          {icon}
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold tracking-tight">
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>

        {/* Select Button */}
        <Button 
          className="mt-2 w-full sm:w-auto opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          tabIndex={-1}
        >
          Select
        </Button>
      </div>
    </Card>
  );
}

export function NewStockMovementPage() {
  const { navigateTo } = useNavigation();



  const handleMovementTypeSelect = (type: string) => {
    console.log(`Selected: ${type}`);
    
    // Navigate to the unified stock movement page with the selected type
    navigateTo("add-movement", { type });
  };

  const movementTypes = [
    {
      icon: <ShoppingCart className="h-12 w-12" />,
      title: "Sales",
      description: "Record stock sold to customers. Decreases inventory levels.",
      type: "Sales",
    },
    {
      icon: <ShoppingBag className="h-12 w-12" />,
      title: "Purchase",
      description: "Record stock received from suppliers. Increases inventory levels.",
      type: "Purchase",
    },
    {
      icon: <Database className="h-12 w-12" />,
      title: "Opening Balance",
      description: "Set initial stock quantities when starting to use the system.",
      type: "Opening Balance",
    },
    {
      icon: <Trash2 className="h-12 w-12" />,
      title: "Write-off",
      description: "Remove damaged, expired, or obsolete stock from inventory.",
      type: "Write-off",
    },
    {
      icon: <ArrowRightLeft className="h-12 w-12" />,
      title: "Transfer Between Locations",
      description: "Move stock from one location to another. Multi-item support.",
      type: "Transfer Between Locations",
    },
    {
      icon: <Edit className="h-12 w-12" />,
      title: "Manual Adjustment",
      description: "Manually increase or decrease stock quantities for corrections.",
      type: "Manual Adjustment",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Create Stock Movement</h1>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            🟡 Demo Mode
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Select the type of movement you want to record
        </p>
      </div>

      {/* Movement Type Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {movementTypes.map((movement) => (
          <MovementTypeCard
            key={movement.type}
            icon={movement.icon}
            title={movement.title}
            description={movement.description}
            onClick={() => handleMovementTypeSelect(movement.type)}
          />
        ))}
      </div>
    </div>
  );
}
